import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import styles from './MissionsBoard.module.css';
import LevelBadge from '../components/LevelBadge/LevelBadge';
import { Package, Eye, Target, Swords } from 'lucide-react';
import { getLevel } from '../utils/levels';
import ContextualHelp from '../components/ContextualHelp';

interface MissionItem {
  id: number;
  title: string;
  objective?: string;
  briefing?: string;
  status: string;
  createdBy?: string;
  createdByOrgPoints?: number; // Creator's org points for level calculation
  startAt?: string;
  endAt?: string;
  missionType?: string; // Will be determined from briefing content
  rewardPoints?: number;
  bonusPoints?: number; // Additional bonus points from mission creator
  maxParticipants?: number;
  acceptances?: {
    id: number;
    memberUsername: string;
    status: string;
    acceptedAt: string;
    completedAt?: string;
  }[];
}

// Mission type icons
const getMissionIcon = (briefing?: string, objective?: string) => {
  const content = (briefing + ' ' + objective).toLowerCase();
  if (content.includes('scu') || content.includes('resource') || content.includes('commodity')) {
    return <Package size={20} />; // Resource gathering
  } else if (content.includes('intel') || content.includes('reconnaissance') || content.includes('surveillance')) {
    return <Eye size={20} />; // Intel gathering
  } else if (content.includes('item') || content.includes('equipment') || content.includes('qty')) {
    return <Target size={20} />; // Item collection
  }
  return <Swords size={20} />; // Default combat/general mission
};

const getMissionTypeLabel = (briefing?: string, objective?: string) => {
  const content = (briefing + ' ' + objective).toLowerCase();
  if (content.includes('scu') || content.includes('resource') || content.includes('commodity')) {
    return 'RESOURCE GATHERING';
  } else if (content.includes('intel') || content.includes('reconnaissance') || content.includes('surveillance')) {
    return 'INTELLIGENCE';
  } else if (content.includes('item') || content.includes('equipment') || content.includes('qty')) {
    return 'ACQUISITION';
  }
  return 'COMBAT OPS';
};

const extractRewardPoints = (briefing?: string) => {
  if (!briefing) return null;
  const match = briefing.match(/(\d+)\s+organization\s+points/i);
  return match ? parseInt(match[1]) : null;
};

const MissionsBoard: React.FC = () => {
  const [missions, setMissions] = useState<MissionItem[]>([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [selectedMission, setSelectedMission] = useState<MissionItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [canAcceptMissions, setCanAcceptMissions] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [requestingAccess, setRequestingAccess] = useState(false);
  const [hasPendingRequest, setHasPendingRequest] = useState(false);

  useEffect(() => {
    fetchUserProfile();
    checkPendingRequest();
    fetchMissions();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const res = await api.get('/auth/user/profile');
      const flags = res.data?.flags || [];
      const role = res.data?.role || '';
      const orgPoints = res.data?.orgPoints || 0;
      const isLevel1Plus = orgPoints >= 10;
      
      console.log('🔍 Mission Board - User Profile:', {
        role,
        flags,
        orgPoints,
        isLevel1Plus,
        hasCanAcceptMissionsFlag: flags.includes('CanAcceptMissions')
      });
      
      setIsAdmin(role === 'Admin');
      // User can accept missions if: Level 1+ OR has the flag
      setCanAcceptMissions(isLevel1Plus || flags.includes('CanAcceptMissions'));
    } catch (err) {
      console.error('Failed to fetch user profile', err);
    }
  };

  const checkPendingRequest = async () => {
    try {
      const res = await api.get('/permissionrequests/my');
      const requests = res.data || [];
      // Check if there's a pending request for CanAcceptMissions
      const hasPending = requests.some(
        (req: any) => req.requestedFlag === 'CanAcceptMissions' && req.status === 'Pending'
      );
      setHasPendingRequest(hasPending);
    } catch (err) {
      console.error('Failed to check pending requests', err);
    }
  };

  const fetchMissions = async () => {
    try {
      const res = await api.get('/missions/board');
      setMissions(res.data || []);
    } catch (err) {
      console.error('Failed to fetch missions', err);
    }
  };

  const handleAcceptMission = async (missionId: number) => {
    if (!window.confirm('Accept this mission?')) return;
    
    setAccepting(true);
    try {
      await api.post(`/missions/${missionId}/accept`);
      alert('Mission accepted! Check "My Missions" to track progress.');
      fetchMissions(); // Refresh list
      setIsModalOpen(false);
    } catch (err: any) {
      const msg = err?.response?.data?.title || err?.response?.data || 'Failed to accept mission';
      alert(msg);
    } finally {
      setAccepting(false);
    }
  };

  const handleRequestAccess = async () => {
    const reason = window.prompt('Please explain why you need the CanAcceptMissions permission:');
    if (!reason || reason.trim() === '') {
      return; // User cancelled or entered empty reason
    }

    setRequestingAccess(true);
    try {
      await api.post('/permissionrequests', {
        requestedFlag: 6, // CanAcceptMissions = 6 in UserFlag enum
        reason: reason.trim()
      });
      setHasPendingRequest(true); // Mark as having pending request
      alert('✅ Permission request submitted successfully! C-Suite leadership will review your request.');
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to submit permission request';
      alert('❌ ' + msg);
    } finally {
      setRequestingAccess(false);
    }
  };

  const openMissionDetails = (mission: MissionItem) => {
    setSelectedMission(mission);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedMission(null);
  };

  const filteredMissions = missions.filter(m => 
    !searchFilter || (m.title + ' ' + (m.objective || '')).toLowerCase().includes(searchFilter.toLowerCase())
  );

  return (
    <div className={styles.pageContainer}>
      <div className={styles.card}>
        <div className={styles.header}>
          <h1 className={styles.title}>
            Mission Board
            <ContextualHelp featureArea="missions" buttonText="?" />
          </h1>
          <p className={styles.subtitle}>Browse and accept available organization missions</p>
        </div>

        <div className={styles.filterBar}>
          <input 
            className={styles.searchInput} 
            placeholder="Search missions..." 
            value={searchFilter} 
            onChange={e => setSearchFilter(e.target.value)} 
          />
          <button className={styles.buttonGhost} onClick={fetchMissions}>Refresh</button>
        </div>

        <div className={styles.cardGrid}>
          {filteredMissions.length === 0 && (
            <div className={styles.emptyState}>No missions available at this time.</div>
          )}
          {filteredMissions.map(m => {
            const creatorLevel = m.createdByOrgPoints !== undefined ? getLevel(m.createdByOrgPoints).level : 0;
            const isHighLevel = creatorLevel >= 5; // Distinguished Contributor and above
            
            // Check if mission is urgent (due in less than 24 hours)
            const isUrgent = m.endAt ? (new Date(m.endAt).getTime() - Date.now()) < 24 * 60 * 60 * 1000 : false;
            const showSpecialBorder = isHighLevel && isUrgent;
            
            return (
            <div key={m.id} className={`${styles.missionCard} ${showSpecialBorder ? styles.highLevelCreator : ''}`}>
              <div className={styles.missionBanner}>
                <div className={styles.missionIcon}>{getMissionIcon(m.briefing, m.objective)}</div>
                <div className={styles.missionType}>{getMissionTypeLabel(m.briefing, m.objective)}</div>
              </div>
              <div className={styles.cardHeader}>
                <h3 className={styles.cardTitle}>{m.title}</h3>
                <span className={styles.statusBadge}>{m.status}</span>
              </div>
              <div className={styles.cardBody}>
                {m.objective && (
                  <div className={styles.objectiveSection}>
                    <div className={styles.sectionLabel}>◆ MISSION OBJECTIVE</div>
                    <p className={styles.objectiveText}>{m.objective}</p>
                  </div>
                )}
                {m.rewardPoints && (
                  <div className={styles.rewardSection}>
                    <div className={styles.rewardLabel}>REWARD</div>
                    <div className={styles.rewardAmount}>
                      <span className={styles.rewardPoints}>{(m.rewardPoints || 0) + (m.bonusPoints || 0)}</span>
                      <span className={styles.rewardUnit}>ORG POINTS</span>
                    </div>
                  </div>
                )}
                {m.createdBy && (
                  <div className={styles.contractIssuer}>
                    <span className={styles.issuerLabel}>Contract Issuer:</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span className={styles.issuerName}>{m.createdBy}</span>
                      {m.createdByOrgPoints !== undefined && (
                        <span className={styles.issuerLevel}>{getLevel(m.createdByOrgPoints).level}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className={styles.cardActions}>
                <button className={styles.buttonPrimary} onClick={() => openMissionDetails(m)}>
                  <span className={styles.buttonIcon}>▶</span>
                  VIEW DETAILS & ACCEPT
                </button>
              </div>
            </div>
            );
          })}
        </div>

        {isModalOpen && selectedMission && (
          <div className={styles.modalOverlay} onClick={closeModal}>
            <div className={styles.modal} onClick={e => e.stopPropagation()}>
              <div className={styles.modalBanner}>
                <div className={styles.modalIcon}>{getMissionIcon(selectedMission.briefing, selectedMission.objective)}</div>
                <div className={styles.modalTypeLabel}>{getMissionTypeLabel(selectedMission.briefing, selectedMission.objective)}</div>
              </div>
              <div className={styles.modalHeader}>
                <h3 className={styles.modalTitle}>{selectedMission.title}</h3>
                <button className={styles.closeButton} onClick={closeModal}>×</button>
              </div>
              <div className={styles.modalBody}>
                <div className={styles.statusRow}>
                  <div className={styles.modalSection}>
                    <div className={styles.modalLabel}>STATUS</div>
                    <div className={styles.readonlyField}>{selectedMission.status}</div>
                  </div>
                  {selectedMission.rewardPoints && (
                    <div className={styles.modalSection}>
                      <div className={styles.modalLabel}>REWARD</div>
                      <div className={styles.rewardBox}>
                        <span className={styles.bigRewardPoints}>{(selectedMission.rewardPoints || 0) + (selectedMission.bonusPoints || 0)}</span>
                        <span className={styles.rewardText}>ORG POINTS</span>
                      </div>
                    </div>
                  )}
                </div>
                {selectedMission.objective && (
                  <div className={styles.modalSection}>
                    <div className={styles.modalLabel}>◆ MISSION OBJECTIVE</div>
                    <div className={styles.missionObjectiveBox}>{selectedMission.objective}</div>
                  </div>
                )}
                {selectedMission.briefing && (
                  <div className={styles.modalSection}>
                    <div className={styles.modalLabel}>◆ MISSION BRIEFING</div>
                    <div 
                      className={styles.briefingBox} 
                      dangerouslySetInnerHTML={{ __html: selectedMission.briefing }} 
                    />
                  </div>
                )}
                {selectedMission.createdBy && (
                  <div className={styles.modalSection}>
                    <div className={styles.modalLabel}>CONTRACT ISSUER</div>
                    <div className={styles.issuerBox}>{selectedMission.createdBy}</div>
                  </div>
                )}
              </div>
              <div className={styles.modalFooter}>
                {!isAdmin && !canAcceptMissions && (
                  <>
                    <div className={styles.permissionNotice}>
                      🔒 You need Level 1 (10+ org points) or permission to accept missions.
                      {hasPendingRequest ? ' Your request is pending review.' : ' Request access below or reach Level 1 by completing your profile and onboarding.'}
                    </div>
                    <button 
                      className={styles.requestAccessButton}
                      onClick={handleRequestAccess}
                      disabled={requestingAccess || hasPendingRequest}
                    >
                      {requestingAccess ? '📤 SUBMITTING...' : hasPendingRequest ? '⏳ REQUEST PENDING' : '📤 REQUEST CANACCEPTMISSIONS PERMISSION'}
                    </button>
                  </>
                )}
                <button 
                  className={styles.acceptButton} 
                  onClick={() => handleAcceptMission(selectedMission.id)}
                  disabled={accepting || (!isAdmin && !canAcceptMissions)}
                  title={(!isAdmin && !canAcceptMissions) ? 'You need Level 1 (10+ org points) or the CanAcceptMissions permission to accept missions' : ''}
                >
                  {accepting ? '⏳ ACCEPTING...' : '✓ ACCEPT MISSION CONTRACT'}
                </button>
                <button className={styles.buttonGhost} onClick={closeModal}>Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MissionsBoard;

